import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiDroplet, FiZap, FiPhone, FiWifi, FiDollarSign, FiShield,
  FiCalendar, FiHeart, FiCreditCard, FiHome, FiShoppingCart,
  FiActivity, FiBriefcase, FiBook, FiTruck, FiTv, FiGrid,
} from 'react-icons/fi';
import { BsLightningCharge } from 'react-icons/bs';
import { MdPayments } from 'react-icons/md';
import { Card, LoaderComponent } from './ui';
import { servicesService } from '../services';
import type { ServiceCategory } from '../types';
import { useTranslation } from 'react-i18next';

// Icon mapping by category code or name
const ICON_MAP: Record<string, React.ReactNode> = {
  'AGU': <FiDroplet className="text-xl" />,
  'agua': <FiDroplet className="text-xl" />,
  'ENG': <BsLightningCharge className="text-xl" />,
  'energia': <BsLightningCharge className="text-xl" />,
  'eletricidade': <BsLightningCharge className="text-xl" />,
  'TEL': <FiPhone className="text-xl" />,
  'telefone': <FiPhone className="text-xl" />,
  'telecomunicacoes': <FiPhone className="text-xl" />,
  'INT': <FiWifi className="text-xl" />,
  'internet': <FiWifi className="text-xl" />,
  'RCG': <FiDollarSign className="text-xl" />,
  'recargas': <FiDollarSign className="text-xl" />,
  'SEG': <FiShield className="text-xl" />,
  'seguro': <FiShield className="text-xl" />,
  'seguros': <FiShield className="text-xl" />,
  'EVT': <FiCalendar className="text-xl" />,
  'eventos': <FiCalendar className="text-xl" />,
  'DNT': <FiHeart className="text-xl" />,
  'doacoes': <FiHeart className="text-xl" />,
  'donativos': <FiHeart className="text-xl" />,
  'FOD': <FiShoppingCart className="text-xl" />,
  'comida': <FiShoppingCart className="text-xl" />,
  'SHP': <FiShoppingCart className="text-xl" />,
  'shopping': <FiShoppingCart className="text-xl" />,
  'GOV': <FiBriefcase className="text-xl" />,
  'governo': <FiBriefcase className="text-xl" />,
  'TEC': <FiGrid className="text-xl" />,
  'tecnologia': <FiGrid className="text-xl" />,
  'TRP': <FiTruck className="text-xl" />,
  'transporte': <FiTruck className="text-xl" />,
  'FAR': <FiActivity className="text-xl" />,
  'farmacia': <FiActivity className="text-xl" />,
  'saude': <FiActivity className="text-xl" />,
  'MKT': <FiShoppingCart className="text-xl" />,
  'mercado': <FiShoppingCart className="text-xl" />,
  'COT': <MdPayments className="text-xl" />,
  'cotacoes': <MdPayments className="text-xl" />,
  'CNT': <FiHome className="text-xl" />,
  'contador': <FiHome className="text-xl" />,
  'tv': <FiTv className="text-xl" />,
  'televisao': <FiTv className="text-xl" />,
  'educacao': <FiBook className="text-xl" />,
  'habitacao': <FiHome className="text-xl" />,
  'comercio': <FiShoppingCart className="text-xl" />,
};

const DEFAULT_ICON = <FiCreditCard className="text-xl" />;

const getIcon = (category: ServiceCategory): React.ReactNode => {
  const codigo = (category as any).codigo || '';
  const nome = ((category as any).nome || (category as any).DESIGNACAO || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
  return ICON_MAP[codigo] || ICON_MAP[nome] || DEFAULT_ICON;
};

const getCategoryName = (c: ServiceCategory) => (c as any).nome || (c as any).DESIGNACAO || '';
const getCategoryId = (c: ServiceCategory) => String((c as any).id || (c as any).ID || '');

// Convert hex color to tailwind-like bg style
const getColorStyle = (hexColor?: string) => {
  if (!hexColor) return { bg: 'bg-gray-100', text: 'text-gray-600' };
  return { bg: '', text: '', hex: hexColor };
};

const QuickPaymentsCard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    servicesService.getCategories()
      .then(data => setCategories(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleClick = (category: ServiceCategory) => {
    const nome = getCategoryName(category).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const codigo = (category as any).codigo || '';

    if (codigo === 'EVT' || nome.includes('evento')) {
      navigate('/eventos');
      return;
    }
    if (codigo === 'DNT' || nome.includes('doacao') || nome.includes('donativo')) {
      navigate('/doacoes');
      return;
    }

    const id = getCategoryId(category);
    navigate(`/payments/${id}/${encodeURIComponent(getCategoryName(category))}`);
  };

  // Show first 8 categories
  const displayCategories = categories.slice(0, 8);

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-pagali-gray-800">
          {t('payments.servicePayment')}
        </h3>
        <button
          onClick={() => navigate('/payments')}
          className="text-sm text-pagali-primary font-medium hover:underline"
        >
          {t('common.viewAll')}
        </button>
      </div>

      {loading ? (
        <LoaderComponent size="sm" />
      ) : (
        <div className="grid grid-cols-4 gap-3">
          {displayCategories.map((category) => {
            const cor = (category as any).cor;
            const iconeUrl = (category as any).iconeUrl;
            return (
              <button
                key={getCategoryId(category)}
                onClick={() => handleClick(category)}
                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-pagali-gray-50 transition-colors group"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform"
                  style={{ backgroundColor: cor ? `${cor}20` : '#f3f4f6' }}
                >
                  {iconeUrl && iconeUrl.startsWith('http') ? (
                    <img src={iconeUrl} alt="" className="w-6 h-6 object-contain" onError={(e: any) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <span style={{ color: cor || '#6b7280' }}>{getIcon(category)}</span>
                  )}
                </div>
                <span className="text-xs font-medium text-pagali-gray-700 text-center">
                  {getCategoryName(category)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default QuickPaymentsCard;
